import { z } from 'zod';
import {
  ATTRIBUTE_COUNT_BY_RARITY,
  EQUIPMENT_ATTRIBUTES,
  EQUIPMENT_COMPONENTS,
  EQUIPMENT_RARITIES,
} from '../equipment/catalog';
import type { itemCategorySchema } from '../inventory/definitions';

/**
 * **O que uma recompensa de campo precisa de carregar para nascer no cofre.**
 *
 * ## O problema que isto resolve
 *
 * Uma carga de campo guardava `definitionId` e `level`, e isso chega para um
 * artefacto, uma carta ou uma joia. **Não chega para tudo o resto**, e a
 * auditoria do V28I.J.1 encontrou três casos em que não chegava:
 *
 * - um **equipamento** tem atributos SORTEADOS por cópia. Sem eles, a extração
 *   escreve uma linha em `item_instances` sem a linha de rolagem, e o gatilho
 *   `equipment_roll_required_guard` faz a primeira tentativa de equipar
 *   rebentar com "equipment instance has no roll";
 * - uma **joia** precisa de `jewel_instances.socket_mode` para poder ser
 *   encaixada. Sem essa linha ela existe e não serve para nada — e isto já era
 *   verdade para a joia dos cometas do V28I.I;
 * - uma **skin** tem uma capacidade de sockets sorteada e imutável por cópia;
 * - um **consumível** nem sequer é uma instância: ele vive numa PILHA, por
 *   quantidade, e conceder-lhe uma linha de instância seria inventar uma
 *   segunda forma de propriedade ao lado da canónica.
 *
 * ## O que este ficheiro é
 *
 * Um descritor **fechado, versionado e exaustivo** do que uma cópia nova de
 * cada categoria precisa — e de nada mais. Ele é selado no nascimento do
 * recurso, viaja intacto pela carga, pela caixa de morte, pelo saque, pela
 * travessia e pela reconexão, e é aberto uma única vez: na extração, onde as
 * autoridades canónicas o transformam em propriedade segura.
 *
 * ## O que ele deliberadamente NÃO carrega
 *
 * Nada que pertença a uma cópia VELHA: energia consumida, acabamento aplicado,
 * evolução anterior, alimentação, uso parcial, equipamento atual, dono
 * anterior. Uma recompensa acabada de gerar nasce no **estado inicial canónico**
 * da sua categoria, e um campo para "quanto já foi usado" seria um convite a
 * inventar história.
 *
 * ## Onde ele é guardado
 *
 * Em `open_world_field_items.properties`, que existe para isto e já é validado
 * por `openWorldFieldItemSchema` — chaves e valores curtos, no máximo dezasseis.
 * Cada campo é uma chave própria em vez de um JSON dentro de uma string: uma
 * cadeia opaca seria exatamente o "payload arbitrário" que o contrato proíbe,
 * e assim o limite de dezasseis chaves é uma prova de que a forma é pequena.
 */

/** A versão do descritor. Sobe quando a FORMA muda, nunca quando o conteúdo. */
export const OPEN_WORLD_FIELD_REWARD_VERSION = 1;

/**
 * Como uma categoria se torna propriedade segura.
 *
 * Fechado e exaustivo: uma categoria nova não cai num ramo permissivo, tem de
 * ser classificada aqui por quem a criar.
 */
export const OPEN_WORLD_REWARD_SHAPES = [
  /** Uma instância simples: `item_instances` e nada mais. */
  'plain',
  /** Uma instância mais a linha de rolagem em `equipment_instances`. */
  'equipment',
  /** Uma instância mais `jewel_instances.socket_mode`. */
  'jewel',
  /** Uma instância mais `skin_instances`, com a capacidade sorteada. */
  'skin',
  /** Uma PILHA: quantidade em `item_stacks`, nunca uma instância. */
  'stack',
] as const;

export type OpenWorldRewardShape = (typeof OPEN_WORLD_REWARD_SHAPES)[number];

/**
 * **A classificação, e é ela que decide tudo o resto.**
 *
 * `Record<ItemCategory, …>` outra vez, e pela mesma razão de sempre: uma
 * categoria nova em `ITEM_CATEGORIES` é um erro de compilação aqui, e o gate
 * que a criar tem de dizer como ela vira propriedade segura em vez de herdar um
 * comportamento que ninguém escolheu.
 */
export const OPEN_WORLD_REWARD_SHAPE_BY_CATEGORY: Readonly<
  Record<z.infer<typeof itemCategorySchema>, OpenWorldRewardShape>
> = Object.freeze({
  /* Uma nave nova tem zero corações; `ship_instances` só nasce à primeira
     incorporação, e a ausência lê-se como zero. Nada a selar além do nível. */
  ship: 'plain',
  cannon: 'plain',
  artifact: 'plain',
  pet: 'plain',
  card: 'plain',
  skin: 'skin',
  consumable: 'stack',
  equipment: 'equipment',
  jewel: 'jewel',
});

/* -------------------------------------------------------------------------- */
/*  O descritor                                                               */
/* -------------------------------------------------------------------------- */

/**
 * **Quantos atributos cabem, lido do CATÁLOGO** (gate V28I.CH.11J-R).
 *
 * Isto era `8`, escrito à mão. A escala tem dez degraus e a contagem sobe com
 * eles — `ATTRIBUTE_COUNT_BY_RARITY` diz uncommon 1, rare 2, … anomaly 8 e
 * **prismatic 9** —, portanto oito parava um degrau antes do topo e as cinco
 * definições `equip_*_prismatic` não se conseguiam escrever.
 *
 * O preço não se pagava a escrever: `serialize()` da sala chama o escritor por
 * núcleo de planeta, em CADA instantânea. Uma sala com um núcleo Prismático
 * deixava de fotografar, e quem esperava por um anúncio esperava até expirar.
 *
 * Este módulo já lia do catálogo o componente, a raridade e o atributo. Só a
 * contagem estava escrita aqui, e foi a única que envelheceu. Derivada, ela
 * acompanha a escala em vez de a reprovar.
 */
const MAX_ATTRIBUTES_PER_EQUIPMENT = Math.max(...Object.values(ATTRIBUTE_COUNT_BY_RARITY));

const attributeSchema = z
  .object({
    a: z.enum(EQUIPMENT_ATTRIBUTES),
    /** O valor de nível 1, na unidade inteira do próprio atributo. */
    v: z.number().int(),
  })
  .strict();

/** O que um equipamento sorteou, e é imutável para aquela cópia. */
export const openWorldEquipmentRewardSchema = z
  .object({
    c: z.enum(EQUIPMENT_COMPONENTS),
    r: z.enum(EQUIPMENT_RARITIES),
    /** A tabela de bandas de onde isto saiu. Congelada na instância. */
    rv: z.number().int().min(1),
    /** O primeiro é o atributo de identidade do componente. A ordem conta. */
    at: z.array(attributeSchema).min(1).max(MAX_ATTRIBUTES_PER_EQUIPMENT),
  })
  .strict();

export type OpenWorldEquipmentReward = z.infer<typeof openWorldEquipmentRewardSchema>;

/**
 * **A recompensa selada.** Uma união discriminada pela FORMA, nunca pela
 * categoria: duas categorias podem partilhar uma forma, e uma forma nova é uma
 * decisão de como a propriedade nasce.
 */
export const openWorldFieldRewardSchema = z.discriminatedUnion('k', [
  z.object({ v: z.literal(OPEN_WORLD_FIELD_REWARD_VERSION), k: z.literal('plain') }).strict(),
  z
    .object({
      v: z.literal(OPEN_WORLD_FIELD_REWARD_VERSION),
      k: z.literal('equipment'),
      eq: openWorldEquipmentRewardSchema,
    })
    .strict(),
  z.object({ v: z.literal(OPEN_WORLD_FIELD_REWARD_VERSION), k: z.literal('jewel') }).strict(),
  z
    .object({
      v: z.literal(OPEN_WORLD_FIELD_REWARD_VERSION),
      k: z.literal('skin'),
      /** A capacidade de sockets, sorteada uma vez e imutável para esta cópia. */
      cap: z.number().int().min(1).max(6),
    })
    .strict(),
  z
    .object({
      v: z.literal(OPEN_WORLD_FIELD_REWARD_VERSION),
      k: z.literal('stack'),
      /** Quantas unidades. Uma cópia encontrada é uma unidade, hoje. */
      q: z.number().int().min(1).max(1_000_000),
    })
    .strict(),
]);

export type OpenWorldFieldReward = z.infer<typeof openWorldFieldRewardSchema>;

/* -------------------------------------------------------------------------- */
/*  A codificação em propriedades                                             */
/* -------------------------------------------------------------------------- */

/** O prefixo de todas as chaves deste descritor. Nada mais lhes toca. */
const KEY = {
  version: 'rw.v',
  shape: 'rw.k',
  component: 'rw.eq.c',
  rarity: 'rw.eq.r',
  rollVersion: 'rw.eq.rv',
  attributeCount: 'rw.eq.n',
  attribute: (index: number) => `rw.eq.a${index}`,
  capacity: 'rw.sk.cap',
  quantity: 'rw.st.q',
} as const;

/** O separador de um atributo codificado: `dano:1234`. Nenhum id o contém. */
const ATTRIBUTE_SEPARATOR = ':';

/**
 * **Escreve o descritor como propriedades curtas.**
 *
 * Chaves e valores pequenos, um campo por chave, dentro do limite de dezasseis
 * que a carga já impõe. O pior caso é um equipamento **Prismático**, que tem
 * nove atributos: seis chaves fixas mais uma por atributo dão quinze, e é o
 * teto a fazer o seu trabalho em vez de ser decorativo.
 *
 * Isto dizia «oito atributos … doze chaves», e estava errado das duas vezes —
 * a escala vai a nove, e oito atributos sempre foram catorze chaves. A
 * aritmética que ninguém refez foi a mesma que deixou o tecto em oito.
 */
export function encodeOpenWorldFieldReward(reward: OpenWorldFieldReward): Record<string, string> {
  /*
   * **Escrever é tão fechado como ler** (gate V28I.CH.11J).
   *
   * Isto começava por `String(reward.v)` e `reward.k` sem perguntar nada. Um
   * descritor malformado — a forma de um item de inventário, por exemplo —
   * saía daqui como `'rw.v': 'undefined'`, a CADEIA, e `'rw.k'` presente com o
   * valor `undefined`, num `Record<string, string>` cujos valores já não eram
   * cadeias. O `JSON.stringify` deita a chave fora e ninguém dá por nada; a
   * clonagem estruturada, que é o caminho da instantânea de verdade, guarda-a,
   * e a sala recusa a reconstrução com «planets[0].cores[0].reward.rw.k must
   * be a non-empty string». Foi assim que apareceu, uma gate inteira depois de
   * ter sido escrito.
   *
   * O leitor já promete recusar o malformado «com uma exceção, e nunca
   * aproximada». O escritor passa a prometer o mesmo: um descritor que não se
   * consegue escrever não vira um descritor escrito a meio.
   */
  const checked = openWorldFieldRewardSchema.safeParse(reward);
  if (!checked.success) {
    throw new RangeError(`open world field reward is malformed: ${checked.error.message}`);
  }
  const out: Record<string, string> = {
    [KEY.version]: String(reward.v),
    [KEY.shape]: reward.k,
  };
  if (reward.k === 'equipment') {
    out[KEY.component] = reward.eq.c;
    out[KEY.rarity] = reward.eq.r;
    out[KEY.rollVersion] = String(reward.eq.rv);
    out[KEY.attributeCount] = String(reward.eq.at.length);
    for (const [index, attribute] of reward.eq.at.entries()) {
      out[KEY.attribute(index)] = `${attribute.a}${ATTRIBUTE_SEPARATOR}${attribute.v}`;
    }
  }
  if (reward.k === 'skin') {
    out[KEY.capacity] = String(reward.cap);
  }
  if (reward.k === 'stack') {
    out[KEY.quantity] = String(reward.q);
  }
  return out;
}

/**
 * **Lê o descritor**, ou `null` quando não há nenhum.
 *
 * `null` é o caso legítimo de uma linha escrita antes deste gate: uma joia de
 * cometa ou um item de planeta da primeira geração. Quem o recebe trata-o como
 * `plain`, que é exatamente o que essas linhas eram — e é por isso que ler uma
 * linha antiga não precisa de uma migração.
 *
 * Uma linha com um descritor MALFORMADO é outra coisa: é recusada com uma
 * exceção, e nunca aproximada. Um equipamento cujo sorteio não se consegue ler
 * não deve virar um equipamento sem sorteio.
 */
export function decodeOpenWorldFieldReward(
  properties: Readonly<Record<string, string>>,
): OpenWorldFieldReward | null {
  const shape = properties[KEY.shape];
  if (shape === undefined) {
    return null;
  }
  const version = Number(properties[KEY.version]);
  const raw: Record<string, unknown> = { v: version, k: shape };
  if (shape === 'equipment') {
    const count = Number(properties[KEY.attributeCount]);
    const at: { a: string; v: number }[] = [];
    for (let index = 0; index < count; index += 1) {
      const encoded = properties[KEY.attribute(index)] ?? '';
      const cut = encoded.lastIndexOf(ATTRIBUTE_SEPARATOR);
      at.push({ a: encoded.slice(0, cut), v: Number(encoded.slice(cut + 1)) });
    }
    raw.eq = {
      c: properties[KEY.component],
      r: properties[KEY.rarity],
      rv: Number(properties[KEY.rollVersion]),
      at,
    };
  }
  if (shape === 'skin') {
    raw.cap = Number(properties[KEY.capacity]);
  }
  if (shape === 'stack') {
    raw.q = Number(properties[KEY.quantity]);
  }
  const parsed = openWorldFieldRewardSchema.safeParse(raw);
  if (!parsed.success) {
    throw new RangeError(`open world field reward is malformed: ${parsed.error.message}`);
  }
  return parsed.data;
}
